// ============================================================================
// L'IMPORT EN ARRIÈRE-PLAN — ce qui porte un import de fichier une fois la fenêtre fermée.
//
// UTILITÉ : la maquette ferme l'assistant au lancement et suit l'import dans une notification en bas à droite ;
// l'opérateur continue de travailler, même sur un autre écran. L'envoi ne peut donc pas vivre dans le composant de
// la fenêtre (il disparaît à sa fermeture) : il vit ici, et la notification comme l'écran des articles le LISENT.
//
// ⚠️ « EN COURS » EST LA VÉRITÉ D'UNE REQUÊTE EN VOL, PAS UN CHRONOMÈTRE : l'état ne passe à « terminé » qu'à la
//    réponse du serveur, avec SON compte rendu, et à « refusé » avec SON motif (§23.0). Aucun pourcentage n'est
//    inventé — le serveur écrit l'import d'un seul geste et ne publie aucune progression.
// ⚠️ UN SEUL IMPORT À LA FOIS : un second lancé pendant le premier écrirait dans la même base sans que l'opérateur
//    sache lequel le rapport décrit. L'assistant le refuse en le disant.
// ============================================================================
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { ApiError } from '@orgazone/api-envelope/client';
import { api, type ImportArticleRow, type ImportArticlesOptions, type ImportArticlesReport } from '../api';

/** Une ligne écartée par l'analyse de l'écran : elle n'est jamais partie, et le rapport la nomme quand même. */
export interface ImportScreenRejected { line: number; why: string; ex: string }

/** Ce qui identifie un import lancé — de quoi le dire, et de quoi le RENVOYER tel quel. */
interface ImportLaunch { key: string; fileName: string; rows: ImportArticleRow[]; options: ImportArticlesOptions; screenRejected: ImportScreenRejected[] }

/**
 * Un import lancé : en vol, terminé (avec ce que le serveur a fait), ou en échec (avec son motif réel).
 * ⚠️ UN ÉCHEC N'EST PAS TOUJOURS UN REFUS : `certain` dit que le magasin a RÉPONDU par un refus (rien n'a été écrit, une
 *    seule transaction) ; sans réponse (coupure, délai de la passerelle), l'import a PEUT-ÊTRE été écrit, et l'écran ne
 *    peut pas dire le contraire.
 */
export type ImportJob = { key: string; fileName: string; lines: number; screenRejected: ImportScreenRejected[] } & (
  | { state: 'running' }
  | { state: 'done'; report: ImportArticlesReport }
  | { state: 'failed'; reason: string; certain: boolean });

/** Le LOT qu'ouvre « Voir les articles » : la clé de l'import, le nom de son fichier, ses modèles. */
export interface ImportBatch { key: string; fileName: string; models: number }

let current: ImportJob | null = null;
/** Le dernier import lancé — ce que « Réessayer » renvoie, sous la MÊME clé. */
let lastLaunch: ImportLaunch | null = null;
/** Combien de fois « Voir les articles » a été demandé — l'écran des articles se remet en tête à chaque demande. */
let showSeq = 0;
/**
 * La dernière demande « Voir les articles » que l'écran des articles n'a pas encore prise, avec son lot.
 * ⚠️ PRISE UNE SEULE FOIS : demandée depuis un autre écran, elle part AVANT que l'écran des articles soit monté — il la
 *    prend à son montage ; prise, elle ne se rejoue pas au montage suivant.
 */
let pendingShow: { batch: ImportBatch | null } | null = null;
const listeners = new Set<() => void>();
const emit = (): void => { for (const l of listeners) l(); };
const subscribe = (l: () => void): (() => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** L'import en cours ou le dernier terminé — `null` quand la notification a été fermée. */
export function useImportJob(): ImportJob | null {
  return useSyncExternalStore(subscribe, () => current, () => current);
}

/** Un import est-il en vol ? — l'assistant refuse d'en lancer un second. */
export const importRunning = (): boolean => current?.state === 'running';

/**
 * LANCE l'import et rend la main tout de suite : la réponse arrive dans la notification.
 * ⚠️ LA CLÉ DE REJEU vient de l'assistant : un envoi rejoué après une coupure rend le même compte rendu, sans doubler.
 */
export function launchImport(p: ImportLaunch): void {
  if (importRunning()) return;
  lastLaunch = p;
  const base = { key: p.key, fileName: p.fileName, lines: p.rows.length, screenRejected: p.screenRejected };
  current = { ...base, state: 'running' };
  emit();
  api.importArticles(p.rows, p.options, p.key)
    .then((report) => { current = { ...base, state: 'done', report }; })
    // ⚠️ LE MOTIF RÉEL, ET CE QU'ON EN SAIT : un refus du magasin (4xx) n'a rien écrit ; sans réponse, on ne sait pas.
    .catch((e: unknown) => {
      const certain = e instanceof ApiError && e.status >= 400 && e.status < 500;
      current = { ...base, state: 'failed', reason: e instanceof Error ? e.message : String(e), certain };
    })
    .finally(emit);
}

/**
 * RÉESSAYER UN IMPORT RESTÉ SANS RÉPONSE — le même fichier, les mêmes choix, la MÊME clé.
 * ⚠️ C'EST LA CLÉ QUI REND LE GESTE SÛR : déjà écrit, le magasin rend le compte rendu de la première fois sans rien
 *    réécrire (`import_run`) ; une clé neuve doublerait les quantités d'un document existant.
 */
export function retryImport(): void {
  if (!lastLaunch || importRunning()) return;
  launchImport(lastLaunch);
}

/** Ferme la notification d'un import fini — un import en vol ne se ferme pas, il se suit. */
export function dismissImportJob(): void {
  if (current?.state === 'running') return;
  current = null;
  emit();
}

/**
 * « VOIR LES ARTICLES » : la notification se ferme, et l'écran des articles s'ouvre sur le LOT importé (pastille
 * retirable, autres filtres levés), comme dans la maquette.
 * ⚠️ UN COMPTEUR, PAS UN DRAPEAU : l'import se lance depuis l'écran des articles, donc y naviguer ne change rien à
 *    l'écran — c'est la demande, relue par l'écran, qui l'y remet même quand on y est déjà.
 * ⚠️ PAS DE LOT SANS ARTICLE ÉCRIT : la liste s'ouvre alors entière, sans pastille — une pastille à « 0 article »
 *    montrerait une liste vide après un import qui a pourtant servi (stock d'existants, référentiels).
 */
export function requestShowImported(): void {
  const batch = current?.state === 'done' && current.report.batchModels > 0
    ? { key: current.key, fileName: current.fileName, models: current.report.batchModels }
    : null;
  pendingShow = { batch };
  showSeq += 1;
  current = null;
  emit();
}

/** Le nombre de demandes « Voir les articles » — l'écran des articles s'y abonne. */
export function useShowImportedSeq(): number {
  return useSyncExternalStore(subscribe, () => showSeq, () => showSeq);
}

/**
 * REMET UNE LISTE EN TÊTE quand un import se termine (`onFinished`), et l'ouvre sur le lot importé à chaque « Voir les
 * articles » de sa notification (`onShow`, avec le lot — `null` quand l'import n'a écrit aucun article) — l'écran des
 * articles y passe ses propres gestes (page 1, tri par défaut, filtres, relecture depuis le serveur), comme la maquette.
 * ⚠️ UNE FOIS PAR IMPORT (sa clé) ET PAR DEMANDE, pas à chaque rendu : les fonctions passent par une référence, l'écran
 *    les recrée à chaque rendu et les mettre dans les dépendances relirait la liste sans fin.
 */
export function useAfterImport(onFinished: () => void, onShow: (batch: ImportBatch | null) => void = onFinished): void {
  const job = useImportJob();
  const termine = job?.state === 'done' ? job.key : null;
  const demandes = useShowImportedSeq();
  const latest = useRef({ onFinished, onShow });
  latest.current = { onFinished, onShow };
  // ⚠️ SEUL UN IMPORT TERMINÉ APRÈS LE MONTAGE COMPTE : l'écran qui s'ouvre lit déjà sa liste en tête ; rejouer à son
  //    montage un import d'hier la relisait une seconde fois pour rien.
  const atMount = useRef(termine);
  useEffect(() => { if (termine && termine !== atMount.current) latest.current.onFinished(); }, [termine]);
  // ⚠️ LA DEMANDE, ELLE, SE PREND MÊME AU MONTAGE : faite depuis un autre écran, elle précède l'ouverture de celui-ci.
  useEffect(() => {
    const pending = pendingShow;
    pendingShow = null;
    if (pending) latest.current.onShow(pending.batch);
  }, [demandes]);
}

/** Pour les bancs : repart d'un état vide entre deux cas. */
export function resetImportJobForTests(): void {
  current = null;
  lastLaunch = null;
  showSeq = 0;
  pendingShow = null;
  emit();
}
